
import React, { Fragment, useEffect } from 'react';
import MainNavigation from '../MainNavigation';
import MainFooter from '../MainFooter';
import classes from './HowItWorks.module.css';
import { FaUserPlus, FaNotesMedical, FaStethoscope, FaChartBar, FaUserMd, FaHistory, FaUsers, FaUserCog, FaCapsules, FaLaptopMedical, FaFileInvoiceDollar, FaPlusSquare, FaCalendarAlt, FaListAlt, FaFileMedical, FaClipboardList, FaCogs, FaYoutube, FaChevronDown } from 'react-icons/fa';

// Import images
import dashboardImg from '../../assests/how-it-works/dashboard.png';
import appointmentsImg from '../../assests/how-it-works/appointments.png';
import vitalsImg from '../../assests/how-it-works/vitals.png';
import doctorListImg from '../../assests/how-it-works/doctor_list.png';
import prescriptionImg from '../../assests/how-it-works/prescription.png';
import historyImg from '../../assests/how-it-works/history.png';
import patientsImg from '../../assests/how-it-works/patients.png';
import staffImg from '../../assests/how-it-works/staff.png';
import pharmacyImg from '../../assests/how-it-works/pharmacy.png';
import pharmacistImg from '../../assests/how-it-works/pharmacist.png';
import statementsImg from '../../assests/how-it-works/statements.png';
import newBillImg from '../../assests/how-it-works/new_bill.png';
import billingHistoryImg from '../../assests/how-it-works/billing_history.png';
import datewiseImg from '../../assests/how-it-works/datewise.png';
import chargeSheetImg from '../../assests/how-it-works/charge_sheet.png';
import checkupReportImg from '../../assests/how-it-works/checkupreport.jpeg';
import configImg from '../../assests/how-it-works/config.jpeg';
import adviceImg from '../../assests/how-it-works/advice.png';

const HowItWorks = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const steps = [
        {
            title: "1. Smart Universal Dashboard",
            role: "All Role Access",
            icon: <FaChartBar />,
            description: "A centralized command center tailored for every user. Whether you are an Admin, Doctor, or Receptionist, the dashboard provides a real-time overview of metrics specific to your responsibilities.",
            image: dashboardImg,
            side: 'right'
        },
        {
            title: "2. Reception & Appointments",
            role: "Receptionist",
            icon: <FaUserPlus />,
            description: "Quickly register new patients or book appointments for existing ones. The system manages a digital waiting list, ensuring zero overlaps and organized patient flow.",
            image: appointmentsImg,
            side: 'left'
        },
        {
            title: "3. Pre-Consultation (Vitals)",
            role: "Receptionist / Nurse",
            icon: <FaNotesMedical />,
            description: "Before the patient sees the doctor, record their vital signs (BP, Pulse, Temperature, SpO2). This saves the doctor's time and provides immediate data for diagnosis.",
            image: vitalsImg,
            side: 'right'
        },
        {
            title: "4. Today's Patient Queue",
            role: "Doctor",
            icon: <FaUserMd />,
            description: "Doctors see a synchronized list of 'Today's Patients' who have completed their vitals check. No more physical files—just click 'Check Up' to begin.",
            image: doctorListImg,
            side: 'left'
        },
        {
            title: "5. Clinical Consultation",
            role: "Doctor",
            icon: <FaStethoscope />,
            description: "Enter diagnosis, symptoms, and advice effortlessly. Search from our 2,000+ medicine library to generate precise digital prescriptions. Print or share via WhatsApp instantly.",
            image: prescriptionImg,
            side: 'right'
        },
        {
            title: "6. Comprehensive Medical History",
            role: "Doctor / Admin",
            icon: <FaHistory />,
            description: "Access complete clinical history in one click. View previous diagnoses, vitals trends, and past prescriptions to provide continuous and informed care.",
            image: historyImg,
            side: 'left'
        },
        {
            title: "7. Patient Data Management",
            role: "Admin / Reception",
            icon: <FaUsers />,
            description: "Full control over patient records. Search, update, or delete patient information securely. Manipulate patient data to keep your medical database accurate and up-to-date.",
            image: patientsImg,
            side: 'right'
        },
        {
            title: "8. Staff & Role Control",
            role: "Admin",
            icon: <FaUserCog />,
            description: "Manage your hospital team by adding staff members and assigning them specific roles (Admin, Doctor, Pharmacist, etc.). Control access levels to maintain data privacy.",
            image: staffImg,
            side: 'left'
        },
        {
            title: "9. Pharmacy & Inventory",
            role: "Admin / Pharmacist",
            icon: <FaCapsules />,
            description: "A robust system to add, update, or delete medicines. Track prices, manufacturers, and expiry dates to ensure your pharmacy is always well-stocked and compliant.",
            image: pharmacyImg,
            side: 'right'
        },
        {
            title: "10. Pharmacist View & Dispensing",
            role: "Pharmacist",
            icon: <FaLaptopMedical />,
            description: "Seamless synchronization: Doctor's prescriptions appear instantly on the pharmacist's screen. Patients can go directly to your hospital pharmacy for accurate dispensing.",
            image: pharmacistImg,
            side: 'left'
        },
        {
            title: "11. Billing Statements",
            role: "Accountant / Admin",
            icon: <FaFileInvoiceDollar />,
            description: "Get a clear view of all hospital revenue. Track payments, outstanding amounts, and payment types (Cash, Online, Card). Use advanced filters to find specific transactions.",
            image: statementsImg,
            side: 'right'
        },
        {
            title: "12. Instant Bill Generation",
            role: "Accountant",
            icon: <FaPlusSquare />,
            description: "Create new bills in seconds. Automatically pull data for consultations, dressings, or laboratory tests. Apply discounts and record partial or full payments effortlessly.",
            image: newBillImg,
            side: 'left'
        },
        {
            title: "13. Patient-wise Billing History",
            role: "Admin / Accountant",
            icon: <FaHistory />,
            description: "Review detailed billing history for any patient. The 'Expenses Information' popup provides a granular breakdown of every charge ever applied to a patient record.",
            image: billingHistoryImg,
            side: 'right'
        },
        {
            title: "14. Datewise Revenue Reports",
            role: "Admin",
            icon: <FaCalendarAlt />,
            description: "Generate financial reports by date range. Monitor daily, weekly, or monthly collections and identify outstanding balances to ensure healthy hospital cash flow.",
            image: datewiseImg,
            side: 'left'
        },
        {
            title: "15. Dynamic Charge Master",
            role: "Admin",
            icon: <FaListAlt />,
            description: "Configure your hospital's service charges. Add or update fees for consultations, minor surgeries, or tests. These charges auto-populate during the billing process for speed and accuracy.",
            image: chargeSheetImg,
            side: 'right'
        },
        {
            title: "16. OPD Reports & Analytics",
            role: "Admin / Accountant",
            icon: <FaChartBar />,
            description: "Access comprehensive reports for your Out-Patient Department. Filter check-up logs by date to track patient visits, manage billing summaries, and export data in professional formats like Excel for advanced analysis.",
            image: checkupReportImg,
            side: 'left'
        },
        {
            title: "17. Hospital Configuration",
            role: "Admin",
            icon: <FaCogs />,
            description: "Complete control over your hospital's digital identity. Customize app themes, languages, and branding. Configure prescription designs, headers, and footers, and toggle system-wide features like AI access or digital billing with ease.",
            image: configImg,
            side: 'right'
        },
        {
            title: "18. Clinical Standards (CRUD)",
            role: "Doctor / Admin",
            icon: <FaClipboardList />,
            description: "Standardize your clinical workflow by managing a central database of medical knowledge. Create, update, or delete clinical advices, instructions, and diagnoses to ensure consistent care across all patient consultations.",
            image: adviceImg,
            side: 'left'
        }
    ];

    return (
        <Fragment>
            <MainNavigation />
            <div className={classes.pageContainer}>
                <header className={classes.hero}>
                    <h1 className={classes.title}>How Dr Tech Buddy Works</h1>
                    <p className={classes.subtitle}>
                        A seamless, integrated workflow that bridges the gap between your reception and clinic.
                    </p>
                    <a 
                        href="https://www.youtube.com/@DrTechBuddy" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={classes.youtubeBtn}
                    >
                        <FaYoutube className={classes.ytIcon} /> Watch Video Tutorials
                    </a>
                    <div className={classes.scrollIndicator}>
                        <span>Explore the Workflow</span>
                        <FaChevronDown />
                    </div>
                </header>

                <div className={classes.workflowTimeline}>
                    {steps.map((step, index) => (
                        <section
                            key={index}
                            className={`${classes.stepSection} ${step.side === 'left' ? classes.reverse : ''}`}
                        >
                            <div className={classes.textContent}>
                                <div className={classes.stepBadge}>
                                    <span className={classes.iconBox}>{step.icon}</span>
                                    <span className={classes.roleTag}>{step.role}</span>
                                </div>
                                <h2 className={classes.stepTitle}>{step.title}</h2>
                                <p className={classes.stepDescription}>{step.description}</p>
                            </div>
                            <div className={classes.imageContent}>
                                <div className={classes.browserFrame}>
                                    <div className={classes.browserHeader}>
                                        <span className={classes.dot}></span>
                                        <span className={classes.dot}></span>
                                        <span className={classes.dot}></span>
                                    </div>
                                    <img src={step.image} alt={step.title} className={classes.screenshot} />
                                </div>
                            </div>
                        </section>
                    ))}
                </div>

                <div className={classes.ctaSection}>
                    <h2>Ready to transform your hospital?</h2>
                    <p>Join hundreds of doctors who trust Dr Tech Buddy for their daily practice.</p>
                    <button className={classes.primaryBtn} onClick={() => window.location.href = '/pricing'}>
                        View Pricing Plans
                    </button>
                </div>
            </div>
            <MainFooter />
        </Fragment>
    );
};

export default HowItWorks;
